import React, { useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { IonSpinner } from '@ionic/react';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Button from '@material-ui/core/Button';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import './ChangePassword.scss';
import { logout } from '../../store';
import { AuthResponse } from '../../models/api/AuthResponse';
import { IonLabel } from '@ionic/react';
import Tooltip from '@material-ui/core/Tooltip';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import SVLS_API from '../../svls-api';
import ChangePwdIcon from "../../assets/images/icons/change-pwd-icon.svg"
import { RootState } from '../../models/RootState';

type ChangePwdProps = {
  logout: Function;
  showTermsCondi?: boolean;
  closeHandler: () => void;
  backHandler: () => void;
};

type ChangePasswordRequest = {
  oldPassword: any;
  newPassword: string;
};

const ChangePwdForm: React.FC<ChangePwdProps> = (props) => {
  const { logout, closeHandler, showTermsCondi } = props;
  const [progress, setProgress] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>(null);
  const [successMsg, setSuccessMsg] = useState<string>(null);
  const langData = useSelector((state: RootState) => state.common.langData)
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const formik = useFormik({
    initialValues: {
      oldPwd: '',
      newPwd: '',
      confNewPwd: '',
      acceptTerms: false,
    },
    validationSchema: Yup.object({
      oldPwd: Yup.string().required(langData?.['required']),
      newPwd: Yup.string()
        .required('Required')
        .matches(
          /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[\w~@#$%^&*+=`|{}:;!.?"()[\]-]{8,}$/,
          langData?.['choose_strong_password_txt']
        ),
      confNewPwd: Yup.string().required('Required'),
      acceptTerms: showTermsCondi
        ? Yup.bool().test(
          'pointsType',
          langData?.['please_accept_tc_txt'],
          () => {
            return formik.values.acceptTerms;
          }
        )
        : Yup.string().optional(),
    }),
    onSubmit: (values) => {
      setErrorMsg(null);
      setSuccessMsg(null);
      const data: ChangePasswordRequest = {
        oldPassword: values.oldPwd.trim(),
        newPassword: values.newPwd.trim(),
      };
      if (values.newPwd === values.confNewPwd) {
        if (values.newPwd === values.oldPwd) {
          setErrorMsg(
            langData?.['new_password_err_txt']
          );
        } else {
          updateNewPassword(data);
        }
      } else {
        setErrorMsg(langData?.['password_mismatch_txt']);
      }
    },
  });

  const updateNewPassword = async (data: ChangePasswordRequest) => {
    try {
      setProgress(true);

      const userName = sessionStorage.getItem('username');
      const response: AuthResponse = await SVLS_API.put(
        `/account/v2/users/${userName}/password:change`,
        data,
        {
          headers: {
            Authorization: sessionStorage.getItem('jwt_token'),
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 204) {
        setSuccessMsg(langData?.['password_change_success_txt']);
        logout();
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message);
    } finally {
      setProgress(false);
    }
  };

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="change-pwd-form-ctn"
      autoComplete="off"
    >
      <div className="change-password-title"><img src={ChangePwdIcon} className="change-pwd-icon" />{langData?.['change_password_txt']}</div>
      <span className="usr-input">
        <IonLabel className="input-label">{langData?.['enter_old_password_txt']}</IonLabel>
        <FormControl
          className="login-input-field pwd-field cp-input"
          variant="outlined"
          error={formik.touched.oldPwd && formik.errors.oldPwd ? true : false}
        >
          <OutlinedInput
            id="old-password"
            type={showOldPassword ? 'text' : 'password'}
            name="oldPwd"
            {...formik.getFieldProps('oldPwd')}
            endAdornment={
              <Button
                onClick={() => setShowOldPassword(!showOldPassword)}
                className="toggle-pwd-btn"
              >
                {showOldPassword ? <Visibility /> : <VisibilityOff />}
              </Button>
            }
          />
          {formik.touched.oldPwd && formik.errors.oldPwd ? (
            <FormHelperText error id="oldPwd-helper-text">
              {formik.errors.oldPwd}
            </FormHelperText>
          ) : null}
        </FormControl>
      </span>

      <div className="pwd-input">
        <IonLabel className="input-label">
          {langData?.['enter_new_password_txt']}
          <Tooltip
            className="input-tooltip"
            title={langData?.['password_restriction']}
          >
            <HelpOutlineIcon />
          </Tooltip>
        </IonLabel>
        <FormControl
          className="login-input-field pwd-field cp-input"
          variant="outlined"
          error={formik.touched.newPwd && formik.errors.newPwd ? true : false}
        >
          <OutlinedInput
            id="new-password"
            type={showNewPassword ? 'text' : 'password'}
            name="newPwd"
            {...formik.getFieldProps('newPwd')}
            endAdornment={
              <Button
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="toggle-pwd-btn"
              >
                {showNewPassword ? <Visibility /> : <VisibilityOff />}
              </Button>
            }
          />
          {formik.touched.newPwd && formik.errors.newPwd ? (
            <FormHelperText error id="newPwd-helper-text">
              {formik.errors.newPwd}
            </FormHelperText>
          ) : null}
        </FormControl>
      </div>
      
      <div className="pwd-input" style={{marginTop:formik.touched.newPwd && formik.errors.newPwd ? 50:null}}>
        <IonLabel className="input-label">{langData?.['confirm_new_password_txt']}</IonLabel>
        <FormControl
          className="login-input-field pwd-field cp-input"
          variant="outlined"
          error={formik.touched.confNewPwd && formik.errors.confNewPwd ? true : false}
        >
          <OutlinedInput
            id="confirm-password"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confNewPwd"
            {...formik.getFieldProps('confNewPwd')}
            endAdornment={
              <Button
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="toggle-pwd-btn"
              >
                {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
              </Button>
            }
          />
          {formik.touched.confNewPwd && formik.errors.confNewPwd ? (
            <FormHelperText error id="confNewPwd-helper-text">
              {formik.errors.confNewPwd}
            </FormHelperText>
          ) : null}
        </FormControl>
      </div>

      {showTermsCondi ? (
        <div className="accept-terms-input">
          <FormControlLabel
            className="accept-terms-field"
            control={
              <Checkbox
                checked={formik.values.acceptTerms}
                onChange={(e) => {
                  formik.handleChange(e);
                }}
                className="accept-terms-checkbox"
                name="acceptTerms"
                color="default"
              />
            }
            label={langData?.['accept_tc_txt']}
            labelPlacement="end"
          />
          {formik.touched.acceptTerms && formik.errors.acceptTerms ? (
            <FormHelperText id="my-helper-text" className="my-helper-text">
              {formik.errors.acceptTerms}
            </FormHelperText>
          ) : null}
        </div>
      ) : null}

      {errorMsg !== '' ? <span className="error-msg">{errorMsg}</span> : null}
      {successMsg !== '' ? (
        <span className="success-msg">{successMsg}</span>
      ) : null}

      <Button
        className="submit-form-btn"
        color="primary"
        endIcon={progress ? <IonSpinner name="lines-small" /> : ''}
        type="submit"
        variant="contained"
      >
        {langData?.['save']}
      </Button>
    </form>
  );
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    logout: () => dispatch(logout()),
  };
};

export default connect(null, mapDispatchToProps)(ChangePwdForm);
